/**
 * Client-side checks for the source IP filter: enough to keep Apply off for
 * obvious typos. The server does the real matching and validation.
 */

const IPV6 = /^[0-9a-f:.]+$/i;

function isDigits(text: string): boolean {
  return [...text].every((ch) => ch >= '0' && ch <= '9');
}

/**
 * One dotted-quad part: one or two digits (a leading zero allowed, "05"), or
 * three digits from 100 to 255.
 */
function isOctet(part: string): boolean {
  if (part.length < 1 || part.length > 3 || !isDigits(part)) return false;
  return part.length < 3 || (part[0] !== '0' && Number(part) <= 255);
}

export function isIpv4(text: string): boolean {
  const parts = text.split('.');
  return parts.length === 4 && parts.every(isOctet);
}

export function isIp(text: string): boolean {
  return isIpv4(text) || (text.includes(':') && IPV6.test(text));
}

function ipv4Number(text: string): number | null {
  return isIpv4(text) ? text.split('.').reduce((n, part) => n * 256 + Number(part), 0) : null;
}

/** Both ends are addresses of one kind and, for IPv4, in order. */
export function isRange(low: string, high: string): boolean {
  if (!isIp(low) || !isIp(high) || isIpv4(low) !== isIpv4(high)) return false;
  const [a, b] = [ipv4Number(low), ipv4Number(high)];
  return a === null || b === null || a <= b;
}

/** One address, or a CIDR block such as 10.0.0.0/24. */
export function isIpOrBlock(text: string): boolean {
  const [ip, prefix, extra] = text.split('/');
  if (extra !== undefined || !ip || !isIp(ip)) return false;
  return prefix === undefined || (prefix.length >= 1 && prefix.length <= 3 && isDigits(prefix));
}
